import { useState, useEffect, useRef } from "react";
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from "@headlessui/react"; 
import { getToken } from "../../utils/tokenUtils";
import axios from "axios";

const LeaveBalanceForm = ({
    loading,
    closeModal,
    onSubmit,
    initialValues,
    errors = {}, 
    }) => {
    
    const [formData, setFormData] = useState(initialValues);
    const [query, setQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const optionsRef = useRef(null);
    const token = getToken();

    useEffect(() => {
        const fetchUsers = async () => {
          try {
            const res = await axios.get(`https://hrmspayroll.prepstripe.com/api/users-with-employee-ids`, {
                headers: {
                    contentType: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }); // update with actual endpoint
            setUsers(res.data);
          } catch (error) {
            console.error("Failed to fetch users:", error);
          }
        };
    
        fetchUsers();
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const submissionData = new FormData(); // Use FormData for file upload
        submissionData.append("user_id", formData.user_id);
        submissionData.append("compulsory_national_holiday_balance", formData.compulsory_national_holiday_balance);
        submissionData.append("public_holiday_balance", formData.public_holiday_balance);
        submissionData.append("privileged_leave_balance", formData.privileged_leave_balance);
        submissionData.append("casual_leave_balance", formData.casual_leave_balance);
        submissionData.append("is_on_notice", formData.is_on_notice);

        onSubmit(submissionData); 

    };

    const handleUserSelect = (selectedUser) => {
        setFormData((prev) => ({
            ...prev,
            user_id: selectedUser.id, // assuming `id` is needed for backend
        }));
    };
    
    const filteredUsers = query === ""
        ? users
        : users.filter((user) =>
            user.name.toLowerCase().includes(query.toLowerCase())
        );

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Selection Field */}
            <div>
                <label htmlFor="user_id" className="block text-sm font-medium dark:text-white text-gray-700">User Name <span className="text-red-500 ml-1">*</span></label>
                <Combobox value={formData.user_id} onChange={(id) => {
                    const selectedUser = users.find(u => u.id === id);
                    if (selectedUser) {
                        handleUserSelect(selectedUser);
                    }
                }}>
                    <div className="relative">
                        <ComboboxInput
                            id="user_id"
                            className="w-full dark:bg-black dark:text-white dark:border-gray-700 border rounded-md p-2"
                            displayValue={(id) => {
                                const user = users.find((u) => u.id === id);
                                return user ? user.name : "";
                            }}      
                            required                    
                            placeholder="Search and select user..."
                        />
                        <ComboboxOptions 
                            ref={optionsRef}
                            className="absolute z-10 w-full dark:bg-black dark:text-white bg-white border mt-1 rounded-md shadow-lg max-h-60 overflow-auto"
                        >
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <ComboboxOption 
                                        key={user.id} 
                                        value={user.id} 
                                        className="cursor-pointer px-4 py-2 dark:hover:bg-gray-800 hover:bg-blue-100"
                                    >
                                        {user.name}
                                    </ComboboxOption>
                                ))
                            ) : (
                                <div className="px-4 py-2 text-gray-500">
                                    {loadingUsers ? "Loading users..." : "No users found"}
                                </div>
                            )}
                        </ComboboxOptions>
                    </div>
                </Combobox>
                {errors.user_id && <p className="text-red-500 text-sm mt-1">{errors.user_id}</p>}
            </div>

            {/* compulsory_national_holiday_balance Field */}
            <div>
                <label htmlFor="compulsory_national_holiday_balance" className="block dark:text-white text-sm font-medium text-gray-700">
                Compulsory National Holiday <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                type="number"
                id="compulsory_national_holiday_balance"
                name="compulsory_national_holiday_balance"
                value={formData.compulsory_national_holiday_balance}
                onChange={handleChange}
                className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                    errors.compulsory_national_holiday_balance ? "border-red-500" : "border-gray-300"
                }`}
                min={0}
                max={25}
                required
                />
                {errors.compulsory_national_holiday_balance && <p className="text-red-500 text-sm mt-1">{errors.compulsory_national_holiday_balance}</p>}
            </div>

            {/* public_holiday_balance Field */}
            <div>
                <label htmlFor="public_holiday_balance" className="block dark:text-white text-sm font-medium text-gray-700">
                Public Holiday Balance <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                type="number"
                id="public_holiday_balance"
                name="public_holiday_balance"
                value={formData.public_holiday_balance}
                onChange={handleChange}
                className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                    errors.public_holiday_balance ? "border-red-500" : "border-gray-300"
                }`}
                min={0}
                max={25}
                required
                />
                {errors.public_holiday_balance && <p className="text-red-500 text-sm mt-1">{errors.public_holiday_balance}</p>}
            </div>

            {/* privileged_leave_balance Field */}
            <div>
                <label htmlFor="privileged_leave_balance" className="block dark:text-white text-sm font-medium text-gray-700">
                Privileged Leave Balance <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                type="number"
                id="privileged_leave_balance"
                name="privileged_leave_balance"
                value={formData.privileged_leave_balance}
                onChange={handleChange}
                className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                    errors.privileged_leave_balance ? "border-red-500" : "border-gray-300"
                }`}
                min={0}
                max={25}
                required
                />
                {errors.privileged_leave_balance && <p className="text-red-500 text-sm mt-1">{errors.privileged_leave_balance}</p>}
            </div>

            {/* casual_leave_balance Field */}
            <div>
                <label htmlFor="casual_leave_balance" className="block dark:text-white text-sm font-medium text-gray-700">
                Casual Leave Balance <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                type="number"
                id="casual_leave_balance"
                name="casual_leave_balance"
                value={formData.casual_leave_balance}
                onChange={handleChange}
                className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                    errors.casual_leave_balance ? "border-red-500" : "border-gray-300"
                }`}
                min={0}
                max={25}
                required
                />
                {errors.casual_leave_balance && <p className="text-red-500 text-sm mt-1">{errors.casual_leave_balance}</p>}
            </div>

            {/* is_on_notice Field */}
            <div>
                <label htmlFor="is_on_notice" className="block dark:text-white text-sm font-medium text-gray-700">
                    On Notice Period? <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                    id="is_on_notice"
                    name="is_on_notice"
                    value={
                    formData.is_on_notice === 1
                        ? "Yes"
                        : formData.is_on_notice === 0
                        ? "No"
                        : ""
                    }
                    onChange={(e) => {
                        const value = e.target.value;
                        const booleanValue = value === "Yes" ? 1 : value === "No" ? 0 : "";
                        // console.log("Selected:", value, "Mapped to:", booleanValue);
                        setFormData({
                        ...formData,
                        is_on_notice: booleanValue,
                        });
                    }}  
                    className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                    errors.is_on_notice ? "border-red-500" : "border-gray-300"
                    }`}
                    required
                >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                </select>
                {errors.is_on_notice && (
                    <p className="text-red-500 text-sm mt-1">{errors.is_on_notice}</p>
                )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end items-center gap-2">
                <button
                type="button"
                onClick={closeModal}
                className="px-6 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                >
                Cancel
                </button>
                <button
                type="submit"
                className="flex items-center gap-2 px-8 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                {initialValues.id ? "Update" : "Add"}
                {loading && (
                    <svg
                    className="animate-spin h-5 w-5 ml-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    ></circle>
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                    </svg>
                )}
                </button>
            </div>
        </form>
    );
};

export default LeaveBalanceForm;